// Help with typing formulas (design 02 9), over the formula bar:
//   - the guide: while a name is being typed, the functions and the
//     workbook's names that start so (a tap puts one in); in a call, its
//     arguments with the one being typed marked and what goes there
//   - the picker (fx): the functions by category, each with its arguments
//     and what it does, and the workbook's names (이름)
// On a phone their buttons leave the focus in the bar (the keyboard stays).
// The 표 screen (Grid.tsx) and a computed tensor's formula (Tensors.tsx)
// use them.
import { useLayoutEffect, useRef, useState } from 'react'

import type { TensorName } from '../api'
import { customDims } from '../dims'
import { FUNC_GROUPS, type GroupId, argGuide, callAt, funcInfo, funcsIn, funcsStarting, signature, tokenAt } from '../grid/funcs'
import type { Book } from '../model'
import { dimKeyName, inputTensors } from '../tensors'

/** A name formulas use (a built-in tensor or dimension, a defined name, an input tensor, a dimension). */
export interface FormulaName {
  name: string
  note: string
}

/** What to put in: a function (NAME( ) or a name. */
export interface Choice {
  text: string
  fn: boolean
}

const keepNothing = () => undefined

/** The names formulas use: the built-in ones, input tensors, dimensions, defined names. */
export function formulaNames(book: Book | undefined, defined: readonly TensorName[]): FormulaName[] {
  const out: FormulaName[] = [
    { name: '지출', note: '지출[사람, 월, 분류] (거래 표의 지출)' },
    { name: '수입', note: '수입[사람, 월, 분류]' },
    { name: '예산', note: '예산[월, 분류]' },
    { name: '거래', note: '거래 표 (열은 거래.날짜처럼)' },
    ...['날짜', '구분', '금액', '사람', '분류', '대분류', '결제수단', '메모'].map((c) => ({ name: `거래.${c}`, note: '거래 표의 열' })),
    { name: '사람', note: '차원' },
    { name: '월', note: '차원 (2026-10처럼)' },
    { name: '분류', note: '차원 (대분류)' },
    { name: '결제수단', note: '차원' },
  ]
  for (const t of inputTensors(book?.tensors))
    out.push({ name: t.name, note: `입력 텐서 [${t.dims.map((k) => dimKeyName(k, book?.dims) ?? '?').join(', ')}]` })
  for (const d of customDims(book?.dims)) out.push({ name: d.name, note: '만든 차원' })
  for (const n of defined) out.push({ name: n.name, note: `=${n.formula}` })
  return out
}

/**
 * Puts a choice into a formula box at its caret: in place of the name being
 * typed there, or as the start of a new formula when the box holds none
 * (what was there gives way, as Excel's insert function). Returns the text.
 */
export function putInto(el: HTMLTextAreaElement, c: Choice): string {
  const put = c.fn ? `${c.text}(` : c.text
  const v = el.value
  if (!v.startsWith('=')) {
    el.value = '=' + put
    el.setSelectionRange(el.value.length, el.value.length)
  } else {
    const token = el.selectionStart === el.selectionEnd ? tokenAt(v, el.selectionStart) : null
    el.setRangeText(put, token ? token.from : el.selectionStart, el.selectionEnd, 'end')
  }
  return el.value
}

/** The guide line for the text being typed, or nothing (not a formula, nothing to say). */
export function FormulaGuide(props: {
  text: string
  caret: number
  names: FormulaName[]
  onPick: (p: Choice) => void
  /** a button's pointer down (a phone: the bar keeps the focus) */
  keep?: (e: React.PointerEvent) => void
}) {
  const { text, caret } = props
  const keep = props.keep ?? keepNothing
  // a long signature scrolls to the argument being typed
  const sig = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const box = sig.current
    const b = box?.querySelector('b')
    if (!box || !(b instanceof HTMLElement)) return
    if (b.offsetLeft < box.scrollLeft || b.offsetLeft + b.offsetWidth > box.scrollLeft + box.clientWidth)
      box.scrollLeft = Math.max(0, b.offsetLeft - box.clientWidth / 4)
  })
  const token = tokenAt(text, caret)
  if (token) {
    const t = token.text.toLowerCase()
    const names = props.names.filter((n) => n.name.toLowerCase().startsWith(t))
    const fns = funcsStarting(token.text)
    if (names.length + fns.length > 0)
      return (
        <div className="g-suggest" role="listbox" aria-label="이름 고르기">
          {names.slice(0, 12).map((n) => (
            <button key={`n:${n.name}`} type="button" role="option" aria-selected={false} title={n.note} onPointerDown={keep} onClick={() => props.onPick({ text: n.name, fn: false })}>
              {n.name}
            </button>
          ))}
          {fns.slice(0, 30).map((f) => (
            <button key={f.name} type="button" role="option" aria-selected={false} className="g-fn" title={f.desc} onPointerDown={keep} onClick={() => props.onPick({ text: f.name, fn: true })}>
              {f.name}
            </button>
          ))}
        </div>
      )
  }
  const call = callAt(text, caret)
  const info = call ? funcInfo(call.name) : undefined
  if (!call || !info) return null
  const g = argGuide(info, call.index, call.named)
  return (
    <div className="g-guide" role="status" aria-label="인자 안내">
      <div className="g-guide-sig" ref={sig}>
        {g.parts.map((p, i) => (p.current ? <b key={i}>{p.text}</b> : <span key={i}>{p.text}</span>))}
      </div>
      <div className="g-guide-hint">
        {g.label && <strong>{g.label}: </strong>}
        {g.hint}
      </div>
    </div>
  )
}

/** The functions by category, and the names; a tap puts one in. */
export function FuncPicker(props: {
  names: FormulaName[]
  onPick: (p: Choice) => void
  onClose: () => void
  keep?: (e: React.PointerEvent) => void
}) {
  const [group, setGroup] = useState<GroupId | 'often' | 'names'>('often')
  const keep = props.keep ?? keepNothing
  return (
    <div className="g-picker" role="dialog" aria-label="함수">
      <div className="g-picker-head">
        <div className="g-picker-tabs" role="tablist" aria-label="함수 종류">
          {FUNC_GROUPS.map((g) => (
            <button key={g.id} type="button" role="tab" aria-selected={group === g.id} onPointerDown={keep} onClick={() => setGroup(g.id)}>
              {g.label}
            </button>
          ))}
        </div>
        <button type="button" className="g-picker-close" aria-label="함수 닫기" title="닫기" onPointerDown={keep} onClick={props.onClose}>
          ✕
        </button>
      </div>
      <div className="g-picker-list" role="tabpanel" aria-label={FUNC_GROUPS.find((g) => g.id === group)?.label}>
        {group === 'names'
          ? props.names.map((n) => (
              <button key={n.name} type="button" onPointerDown={keep} onClick={() => props.onPick({ text: n.name, fn: false })}>
                <code>{n.name}</code>
                <small>{n.note}</small>
              </button>
            ))
          : funcsIn(group).map((f) => (
              <button key={f.name} type="button" onPointerDown={keep} onClick={() => props.onPick({ text: f.name, fn: true })}>
                <code>{signature(f)}</code>
                <small>{f.desc}</small>
              </button>
            ))}
        {group === 'names' && props.names.length === 0 && <p className="muted small">이름이 없습니다.</p>}
      </div>
    </div>
  )
}
